import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Mail, Lock, Eye, EyeOff, ArrowLeft, KeyRound, Sparkles, X } from "lucide-react";
import toast from "react-hot-toast";
import "./AuthPages.css";

export default function LoginPage() {
  const [step, setStep] = useState("email"); // "email" | "password" | "otp"
  const [loginMethod, setLoginMethod] = useState("otp"); // "otp" | "password"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const otpRefs = useRef([]);
  const { login, googleLogin, sendOtp, verifyOtp } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (clientId) {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (window.google?.accounts?.id) {
          window.google.accounts.id.initialize({
            client_id: clientId,
            callback: handleGoogleCallback,
          });
        }
      };
      document.body.appendChild(script);
      return () => {
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
      };
    }
  }, []);

  const handleGoogleCallback = async (response) => {
    setLoading(true);
    try {
      await googleLogin({ credential: response.credential });
      toast.success("Welcome back via Google! 🎉");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.message || "Google Sign-In failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleClick = async () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (clientId && window.google?.accounts?.id) {
      window.google.accounts.id.prompt();
    } else {
      const userEmail = prompt("Enter your Google Account email for Instant Google Sign-In:", email || "creator@gmail.com");
      if (!userEmail) return;
      setLoading(true);
      try {
        await googleLogin({
          googleUser: {
            email: userEmail,
            name: userEmail.split("@")[0].replace(".", " "),
            avatar: "https://lh3.googleusercontent.com/a/default-user=s96-c",
          },
        });
        toast.success("Welcome back via Google! 🎉");
        navigate("/dashboard");
      } catch (err) {
        toast.error(err.response?.data?.message || "Google Sign-In failed.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("Please enter your email");
    setLoading(true);
    try {
      await sendOtp(email);
      toast.success("OTP sent to your email!");
      setStep("otp");
      setResendTimer(60);
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const updated = [...otp];
    updated[index] = value.slice(-1);
    setOtp(updated);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(""));
      otpRefs.current[5]?.focus();
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const otpStr = otp.join("");
    if (otpStr.length < 6) return toast.error("Please enter the full 6-digit OTP");
    setLoading(true);
    try {
      await verifyOtp(email, otpStr);
      toast.success("Welcome back! 🎉");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.message || "Invalid OTP");
      setOtp(["", "", "", "", "", ""]);
      otpRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error("Please enter both email and password");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back! 🎉");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    setLoading(true);
    try {
      await sendOtp(email);
      toast.success("New OTP sent!");
      setResendTimer(60);
      setOtp(["", "", "", "", "", ""]);
      otpRefs.current[0]?.focus();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      {/* Pure CSS backdrop — real HomePage stays mounted in the router behind us */}
      <div className="auth-landing-bg" />

      {/* Modal Overlay Layer */}
      <div className="auth-modal-overlay">
        <div className="auth-card-container fade-in">
          {/* Close Button to return to Home Page */}
          <button
            className="auth-close-btn"
            title="Back to Home Page"
            onClick={() => navigate("/")}
          >
            <X size={16} />
          </button>

          {/* Card Header */}
          <div className="auth-card-header">
            <div className="auth-brand-row">
              <div className="auth-brand-logo">
                <img src="/logo.png" alt="NEYRIX AI Logo" style={{ width: 22, height: 22, objectFit: "contain" }} />
              </div>
              <span className="auth-brand-title">NEYRIX AI</span>
            </div>
          </div>

          {/* Form Body */}
          {step === "otp" ? (
            <>
              <button className="auth-footer-link" style={{ background: "none", border: "none", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "0.4rem", padding: 0, marginBottom: "1rem" }} onClick={() => { setStep("email"); setOtp(["", "", "", "", "", ""]); }}>
                <ArrowLeft size={16} /> Back to Sign In
              </button>

              <div className="auth-title-section">
                <h2 className="auth-title">Check your inbox</h2>
                <p className="auth-subtitle">
                  We've sent a 6-digit code to <strong style={{ color: "var(--text-primary)" }}>{email}</strong>
                </p>
              </div>

              <form onSubmit={handleVerifyOtp}>
                <div className="auth-otp-grid" onPaste={handleOtpPaste}>
                  {otp.map((digit, i) => (
                    <input
                      key={i}
                      ref={(el) => (otpRefs.current[i] = el)}
                      className="auth-otp-box"
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(i, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    />
                  ))}
                </div>

                <button className="auth-submit-btn" type="submit" disabled={loading || otp.join("").length < 6}>
                  {loading ? <span className="spinner" /> : "Verify & Sign In"}
                </button>
              </form>

              <div style={{ textAlign: "center", marginTop: "1.25rem" }}>
                {resendTimer > 0 ? (
                  <span style={{ fontSize: "0.85rem", color: "#6b7280" }}>
                    Resend code in <strong style={{ color: "#8c85ff" }}>{resendTimer}s</strong>
                  </span>
                ) : (
                  <button className="auth-footer-link" style={{ background: "none", border: "none", cursor: "pointer" }} onClick={handleResend} disabled={loading}>
                    Resend OTP Code
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="auth-title-section">
                <h2 className="auth-title">Welcome back</h2>
                <p className="auth-subtitle">Sign in to your NEYRIX AI account</p>
              </div>

              {/* Google Button */}
              <button className="auth-google-btn" type="button" disabled={loading} onClick={handleGoogleClick}>
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
                </svg>
                {loading ? "Connecting..." : "Continue with Google"}
              </button>

              <div className="auth-divider">
                <div className="auth-divider-line" />
                <span className="auth-divider-text">OR CONTINUE WITH EMAIL</span>
                <div className="auth-divider-line" />
              </div>

              {/* Tabs */}
              <div className="auth-tab-bar">
                <button
                  type="button"
                  className={`auth-tab-item ${loginMethod === "otp" ? "active" : ""}`}
                  onClick={() => setLoginMethod("otp")}
                >
                  <Sparkles size={14} /> Passwordless OTP
                </button>
                <button
                  type="button"
                  className={`auth-tab-item ${loginMethod === "password" ? "active" : ""}`}
                  onClick={() => setLoginMethod("password")}
                >
                  <KeyRound size={14} /> Password
                </button>
              </div>

              {/* Forms */}
              {loginMethod === "otp" ? (
                <form onSubmit={handleSendOtp}>
                  <div className="auth-input-group">
                    <label className="auth-label">Email Address</label>
                    <div className="auth-input-wrapper">
                      <Mail size={18} className="auth-input-icon" />
                      <input
                        className="auth-input-field"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <button className="auth-submit-btn" type="submit" disabled={loading}>
                    {loading ? <span className="spinner" /> : "Send Login Code"}
                  </button>
                </form>
              ) : (
                <form onSubmit={handlePasswordLogin}>
                  <div className="auth-input-group">
                    <label className="auth-label">Email Address</label>
                    <div className="auth-input-wrapper">
                      <Mail size={18} className="auth-input-icon" />
                      <input
                        className="auth-input-field"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="auth-input-group">
                    <label className="auth-label">Password</label>
                    <div className="auth-input-wrapper">
                      <Lock size={18} className="auth-input-icon" />
                      <input
                        className="auth-input-field"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                      <button
                        type="button"
                        className="auth-eye-btn"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <button className="auth-submit-btn" type="submit" disabled={loading}>
                    {loading ? <span className="spinner" /> : "Sign In with Password"}
                  </button>
                </form>
              )}
            </>
          )}

          <div className="auth-footer">
            Don't have an account?{" "}
            <Link to="/register" className="auth-footer-link">
              Create an account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}